import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Bell, Send, Clock, MapPin, Trash2, CheckCircle, AlertCircle } from 'lucide-react';
import './NotificationsModal.css';

const API = process.env.REACT_APP_API_URL;

const TYPE_LABELS = {
    instant:   { label: 'Instant',   color: '#2B8EE8' },
    scheduled: { label: 'Scheduled', color: '#f59e0b' },
    location:  { label: 'Location',  color: '#10b981' },
};

const STATUS_LABELS = {
    active:    { label: 'Active',    color: '#10b981' },
    cancelled: { label: 'Cancelled', color: '#94A3B8' },
    expired:   { label: 'Expired',   color: '#94A3B8' },
};

const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ─── Notification list item ───────────────────────────────────────────────────
const NotificationRow = ({ notification, onCancel, cancelling }) => {
    const type = TYPE_LABELS[notification.triggerType] || TYPE_LABELS.instant;
    const status = STATUS_LABELS[notification.status] || STATUS_LABELS.active;
    const canCancel = notification.status === 'active';

    return (
        <div className={`nm-notif-row ${notification.status !== 'active' ? 'nm-notif-row--dim' : ''}`}>
            <div className="nm-notif-row-top">
                <span className="nm-type-pill" style={{ background: type.color + '20', color: type.color }}>
                    {type.label}
                </span>
                <span className="nm-status-pill" style={{ color: status.color }}>
                    {status.label}
                </span>
                {canCancel && (
                    <button
                        className="nm-cancel-btn"
                        onClick={() => onCancel(notification._id)}
                        disabled={cancelling === notification._id}
                        title="Cancel notification"
                    >
                        {cancelling === notification._id ? '…' : <Trash2 size={13} />}
                    </button>
                )}
            </div>
            <p className="nm-notif-title">{notification.title}</p>
            {notification.body && <p className="nm-notif-body">{notification.body}</p>}
            <div className="nm-notif-meta">
                {notification.triggerType === 'scheduled' && notification.scheduledAt && (
                    <span><Clock size={11} /> {formatDateTime(notification.scheduledAt)}</span>
                )}
                {notification.triggerType === 'location' && notification.zone?.markerName && (
                    <span><MapPin size={11} /> {notification.zone.markerName} · {notification.zone.radiusMeters}m radius</span>
                )}
                <span className="nm-notif-expires">
                    Expires {formatDateTime(notification.expiresAt)}
                </span>
            </div>
        </div>
    );
};

// ─── NotificationsModal ───────────────────────────────────────────────────────
const NotificationsModal = ({ map, onClose }) => {
    const [activeTab, setActiveTab] = useState('instant');
    const [notifications, setNotifications] = useState([]);
    const [markers, setMarkers] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [cancelling, setCancelling] = useState(null);
    const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', message }

    // Form state
    const [instantForm, setInstantForm] = useState({ title: '', body: '' });
    const [schedForm, setSchedForm] = useState({ title: '', body: '', scheduledAt: '' });
    const [locForm, setLocForm] = useState({ title: '', body: '', markerId: '', radiusMeters: 10 });

    const token = localStorage.getItem('popmap_token');
    const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    // ── Fetch existing notifications ──
    const fetchNotifications = useCallback(async () => {
        try {
            setListLoading(true);
            const res = await fetch(`${API}/api/notifications/${map.id}`, { headers: authHeader });
            const data = await res.json();
            if (data.success) setNotifications(data.data.notifications);
        } catch (_) {
            /* silently fail */
        } finally {
            setListLoading(false);
        }
    }, [map.id, authHeader]);

    // ── Fetch markers for location trigger dropdown ──
    const fetchMarkers = useCallback(async () => {
        try {
            const res = await fetch(`${API}/api/markers/${map.id}`, { headers: authHeader });
            const data = await res.json();
            if (data.success) setMarkers(data.data.markers || []);
        } catch (_) { /* silently fail */ }
    }, [map.id, authHeader]);

    useEffect(() => {
        fetchNotifications();
        fetchMarkers();
    }, [fetchNotifications, fetchMarkers]);

    const showFeedback = (type, message) => {
        setFeedback({ type, message });
        setTimeout(() => setFeedback(null), 3500);
    };

    // ── Send notification ──
    const handleSend = useCallback(async (triggerType) => {
        let payload = { triggerType };

        if (triggerType === 'instant') {
            if (!instantForm.title.trim()) return showFeedback('error', 'Title is required');
            payload.title = instantForm.title.trim();
            payload.body  = instantForm.body.trim() || null;
        } else if (triggerType === 'scheduled') {
            if (!schedForm.title.trim()) return showFeedback('error', 'Title is required');
            if (!schedForm.scheduledAt)  return showFeedback('error', 'Schedule time is required');
            if (new Date(schedForm.scheduledAt) <= new Date()) return showFeedback('error', 'Scheduled time must be in the future');
            payload.title       = schedForm.title.trim();
            payload.body        = schedForm.body.trim() || null;
            payload.scheduledAt = schedForm.scheduledAt;
        } else if (triggerType === 'location') {
            if (!locForm.title.trim())  return showFeedback('error', 'Title is required');
            if (!locForm.markerId)      return showFeedback('error', 'Select a marker');
            const marker = markers.find(m => m.id === locForm.markerId);
            if (!marker) return showFeedback('error', 'Marker not found');
            payload.title = locForm.title.trim();
            payload.body  = locForm.body.trim() || null;
            payload.zone  = {
                markerId:     marker.id,
                markerName:   marker.name,
                markerLat:    marker.lat,
                markerLng:    marker.lng,
                radiusMeters: locForm.radiusMeters,
            };
        }

        try {
            setSending(true);
            const res = await fetch(`${API}/api/notifications/${map.id}`, {
                method: 'POST',
                headers: { ...authHeader, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message);

            // Reset form
            if (triggerType === 'instant')   setInstantForm({ title: '', body: '' });
            if (triggerType === 'scheduled') setSchedForm({ title: '', body: '', scheduledAt: '' });
            if (triggerType === 'location')  setLocForm({ title: '', body: '', markerId: '', radiusMeters: 10 });

            showFeedback('success', triggerType === 'instant' ? 'Notification sent!' : 'Notification created');
            fetchNotifications();
        } catch (err) {
            showFeedback('error', err.message || 'Failed to send');
        } finally {
            setSending(false);
        }
    }, [instantForm, schedForm, locForm, markers, map.id, authHeader, fetchNotifications]);

    // ── Cancel notification ──
    const handleCancel = useCallback(async (id) => {
        try {
            setCancelling(id);
            const res = await fetch(`${API}/api/notifications/${id}/cancel`, {
                method: 'PATCH',
                headers: authHeader,
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message);
            setNotifications(prev =>
                prev.map(n => n._id === id ? { ...n, status: 'cancelled' } : n)
            );
        } catch (err) {
            showFeedback('error', 'Failed to cancel');
        } finally {
            setCancelling(null);
        }
    }, [authHeader]);

    // Min datetime for the scheduler (now + 1 min)
    const minDateTime = useMemo(() => {
        const d = new Date(Date.now() + 60000);
        return d.toISOString().slice(0, 16);
    }, []);

    const activeCount = notifications.filter(n => n.status === 'active').length;

    return (
        <div className="mm-overlay" onClick={onClose}>
            <div className="mm-modal nm-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="mm-modal-header">
                    <div className="nm-modal-title-row">
                        <Bell size={18} className="nm-bell-icon" />
                        <h2 className="mm-modal-title">Notifications</h2>
                        <span className="nm-map-name">{map.title}</span>
                    </div>
                    <button className="mm-close-btn" onClick={onClose}><X size={16} /></button>
                </div>

                {/* Feedback banner */}
                {feedback && (
                    <div className={`nm-feedback nm-feedback--${feedback.type}`}>
                        {feedback.type === 'success'
                            ? <CheckCircle size={14} />
                            : <AlertCircle size={14} />
                        }
                        {feedback.message}
                    </div>
                )}

                <div className="mm-modal-body nm-body">

                    {/* ── Compose tabs ── */}
                    <div className="nm-section">
                        <p className="nm-section-label">Send notification</p>
                        <div className="nm-tabs">
                            {[
                                { id: 'instant',   icon: <Send size={13} />,   label: 'Now' },
                                { id: 'scheduled', icon: <Clock size={13} />,  label: 'Schedule' },
                                { id: 'location',  icon: <MapPin size={13} />, label: 'Location' },
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    className={`nm-tab ${activeTab === tab.id ? 'nm-tab--active' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    {tab.icon}{tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Instant */}
                        {activeTab === 'instant' && (
                            <div className="nm-form">
                                <p className="nm-form-hint">
                                    Sends immediately to all attendees currently viewing this map.
                                </p>
                                <div className="mm-form-group">
                                    <label>Title <span className="nm-required">*</span></label>
                                    <input
                                        className="mm-input"
                                        placeholder="e.g. Main stage starting now!"
                                        maxLength={80}
                                        value={instantForm.title}
                                        onChange={e => setInstantForm(p => ({ ...p, title: e.target.value }))}
                                    />
                                    <span className="nm-char-count">{instantForm.title.length}/80</span>
                                </div>
                                <div className="mm-form-group">
                                    <label>Message <span className="nm-optional">(optional)</span></label>
                                    <textarea
                                        className="mm-input mm-textarea"
                                        placeholder="Additional details…"
                                        rows={2}
                                        maxLength={200}
                                        value={instantForm.body}
                                        onChange={e => setInstantForm(p => ({ ...p, body: e.target.value }))}
                                    />
                                    <span className="nm-char-count">{instantForm.body.length}/200</span>
                                </div>
                                <button
                                    className="mm-btn mm-btn--primary nm-send-btn"
                                    onClick={() => handleSend('instant')}
                                    disabled={sending || !instantForm.title.trim()}
                                >
                                    <Send size={14} />
                                    {sending ? 'Sending…' : 'Send Now'}
                                </button>
                            </div>
                        )}

                        {/* Scheduled */}
                        {activeTab === 'scheduled' && (
                            <div className="nm-form">
                                <p className="nm-form-hint">
                                    Banner will appear at the scheduled time and expire 2 hours later.
                                </p>
                                <div className="mm-form-group">
                                    <label>Title <span className="nm-required">*</span></label>
                                    <input
                                        className="mm-input"
                                        placeholder="e.g. Food stalls closing in 30 minutes"
                                        maxLength={80}
                                        value={schedForm.title}
                                        onChange={e => setSchedForm(p => ({ ...p, title: e.target.value }))}
                                    />
                                    <span className="nm-char-count">{schedForm.title.length}/80</span>
                                </div>
                                <div className="mm-form-group">
                                    <label>Message <span className="nm-optional">(optional)</span></label>
                                    <textarea
                                        className="mm-input mm-textarea"
                                        placeholder="Additional details…"
                                        rows={2}
                                        maxLength={200}
                                        value={schedForm.body}
                                        onChange={e => setSchedForm(p => ({ ...p, body: e.target.value }))}
                                    />
                                    <span className="nm-char-count">{schedForm.body.length}/200</span>
                                </div>
                                <div className="mm-form-group">
                                    <label>Send at <span className="nm-required">*</span></label>
                                    <input
                                        type="datetime-local"
                                        className="mm-input"
                                        min={minDateTime}
                                        value={schedForm.scheduledAt}
                                        onChange={e => setSchedForm(p => ({ ...p, scheduledAt: e.target.value }))}
                                    />
                                </div>
                                <button
                                    className="mm-btn mm-btn--primary nm-send-btn"
                                    onClick={() => handleSend('scheduled')}
                                    disabled={sending || !schedForm.title.trim() || !schedForm.scheduledAt}
                                >
                                    <Clock size={14} />
                                    {sending ? 'Scheduling…' : 'Schedule'}
                                </button>
                            </div>
                        )}

                        {/* Location */}
                        {activeTab === 'location' && (
                            <div className="nm-form">
                                <p className="nm-form-hint">
                                    Banner appears when an attendee's GPS is within the radius of the chosen marker.
                                </p>
                                <div className="mm-form-group">
                                    <label>Title <span className="nm-required">*</span></label>
                                    <input
                                        className="mm-input"
                                        placeholder="e.g. You're near the VIP lounge!"
                                        maxLength={80}
                                        value={locForm.title}
                                        onChange={e => setLocForm(p => ({ ...p, title: e.target.value }))}
                                    />
                                    <span className="nm-char-count">{locForm.title.length}/80</span>
                                </div>
                                <div className="mm-form-group">
                                    <label>Message <span className="nm-optional">(optional)</span></label>
                                    <textarea
                                        className="mm-input mm-textarea"
                                        placeholder="Additional details…"
                                        rows={2}
                                        maxLength={200}
                                        value={locForm.body}
                                        onChange={e => setLocForm(p => ({ ...p, body: e.target.value }))}
                                    />
                                    <span className="nm-char-count">{locForm.body.length}/200</span>
                                </div>
                                <div className="mm-form-group">
                                    <label>Marker <span className="nm-required">*</span></label>
                                    {markers.length === 0 ? (
                                        <p className="nm-no-markers">No markers on this map yet.</p>
                                    ) : (
                                        <select
                                            className="mm-input"
                                            value={locForm.markerId}
                                            onChange={e => setLocForm(p => ({ ...p, markerId: e.target.value }))}
                                        >
                                            <option value="">Choose a marker…</option>
                                            {markers.map(m => (
                                                <option key={m.id} value={m.id}>
                                                    {m.name} ({m.category})
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                                <div className="mm-form-group">
                                    <label>
                                        Radius
                                        <span className="nm-radius-value">{locForm.radiusMeters}m</span>
                                    </label>
                                    <input
                                        type="range"
                                        min={5}
                                        max={100}
                                        step={1}
                                        value={locForm.radiusMeters}
                                        onChange={e => setLocForm(p => ({ ...p, radiusMeters: Number(e.target.value) }))}
                                        className="nm-radius-slider"
                                    />
                                    <div className="nm-radius-labels">
                                        <span>5m</span>
                                        <span className="nm-radius-hint">
                                            {locForm.radiusMeters <= 10
                                                ? 'Precise — within a stall'
                                                : locForm.radiusMeters <= 30
                                                ? 'Close proximity'
                                                : 'Wide zone'}
                                        </span>
                                        <span>100m</span>
                                    </div>
                                </div>
                                <button
                                    className="mm-btn mm-btn--primary nm-send-btn"
                                    onClick={() => handleSend('location')}
                                    disabled={sending || !locForm.title.trim() || !locForm.markerId}
                                >
                                    <MapPin size={14} />
                                    {sending ? 'Creating…' : 'Create Trigger'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ── Notification list ── */}
                    <div className="nm-section nm-list-section">
                        <p className="nm-section-label">
                            Notification history
                            {activeCount > 0 && (
                                <span className="nm-active-count">{activeCount} active</span>
                            )}
                        </p>
                        {listLoading ? (
                            <div className="nm-list-loading"><div className="mm-spinner" /></div>
                        ) : notifications.length === 0 ? (
                            <p className="nm-list-empty">No notifications sent yet.</p>
                        ) : (
                            <div className="nm-notif-list">
                                {notifications.map(n => (
                                    <NotificationRow
                                        key={n._id}
                                        notification={n}
                                        onCancel={handleCancel}
                                        cancelling={cancelling}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                </div>

                <div className="mm-modal-footer">
                    <button className="mm-btn mm-btn--ghost" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default NotificationsModal;